import { getSupabaseClient, isSupabaseConfigured } from '@/lib/supabase';
import {
  STRIPE_PRODUCTS,
  BILLING_PLANS_BY_PRICE_ID,
  type BillingPlanCode,
  type StripeProduct
} from '@/stripe-config';
import { getCSRFToken } from '@/lib/security/csrf';
import { isDevelopment } from '@/lib/env';

// Stripe service types
export interface SubscriptionStatus {
  plan: 'free' | 'pro' | 'lifetime';
  planCode?: BillingPlanCode | null;
  planLabel?: string;
  isActive: boolean;
  expiresAt?: Date;
  willRenew?: boolean;
  entitlements: string[];
  customerId?: string;
  subscriptionId?: string;
}

export { type StripeProduct };

// Rate limiting store for client-side API calls
const rateLimitStore = new Map<string, { count: number; resetTime: number }>();

// Rate limiting configuration
const RATE_LIMIT = {
  windowMs: 15 * 60 * 1000, // 15 minutes
  maxRequests: 10, // Max requests per window for checkout sessions
};

// Stripe service class for handling subscription logic
export class StripeService {
  private static instance: StripeService;
  private static initialized = false;

  private constructor() {}
  
  static getInstance(): StripeService {
    if (!StripeService.instance) {
      StripeService.instance = new StripeService();
    }
    return StripeService.instance;
  }

  // Rate limiting function for client-side API calls
  private static checkRateLimit(clientId: string): boolean {
    const now = Date.now();
    const clientData = rateLimitStore.get(clientId);

    if (!clientData || now > clientData.resetTime) {
      // Reset or initialize rate limit
      rateLimitStore.set(clientId, {
        count: 1,
        resetTime: now + RATE_LIMIT.windowMs
      });
      return true;
    }

    if (clientData.count >= RATE_LIMIT.maxRequests) {
      return false; // Rate limit exceeded
    }

    clientData.count++;
    return true;
  }
  
  // Check if Stripe is properly configured
  static isConfigured(): boolean {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

    if (!publishableKey || publishableKey.trim() === '') {
      return false;
    }

    if (publishableKey.includes('your_publishable_key_here')) {
      return false;
    }

    if (!publishableKey.startsWith('pk_test_') && !publishableKey.startsWith('pk_live_')) {
      return false;
    }

    return true;
  }
  
  // Initialize Stripe SDK
  static async initialize(): Promise<boolean> {
    if (!this.isConfigured()) {
      return false;
    }
    
    if (!isSupabaseConfigured()) {
      return false;
    }
    
    try {
      this.initialized = true;
      return true;
    } catch (error) {
      this.trackError(error instanceof Error ? error : new Error(String(error)), 'initialization');
      return false;
    }
  }
  
  // Get current subscription status
  static async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    if (!isSupabaseConfigured()) {
      return {
        plan: 'free',
        planCode: null,
        planLabel: 'Free',
        isActive: false,
        entitlements: []
      };
    }

    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const supabase = getSupabaseClient();

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          plan: 'free',
          planCode: null,
          planLabel: 'Free',
          isActive: false,
          entitlements: []
        };
      }

      // Get subscription data from the view with retry logic
      let subscription, error;
      const maxRetries = 3;
      let retryCount = 0;

      while (retryCount < maxRetries) {
        try {
          const result = await supabase
            .from('stripe_user_subscriptions')
            .select('*')
            .maybeSingle();

          subscription = result.data;
          error = result.error;
          if (error) throw error;
          break;
        } catch (err) {
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          } else {
            error = err;
          }
        }
      }

      if (error || !subscription || !subscription.subscription_status) {
        if (error) {
          this.trackError(error instanceof Error ? error : new Error(String(error)), 'fetch_subscription');
        }
        return {
          plan: 'free',
          planCode: null,
          planLabel: 'Free',
          isActive: false,
          entitlements: []
        };
      }

      // Determine plan based on subscription status
      const isActive = ['active', 'trialing'].includes(subscription.subscription_status);
      const priceId = subscription.price_id;
      const planDefinition = priceId ? BILLING_PLANS_BY_PRICE_ID[priceId] : undefined;
      const derivedPlanType = planDefinition?.planType === 'lifetime'
        ? 'lifetime'
        : planDefinition
          ? 'pro'
          : 'free';
      const plan = isActive ? derivedPlanType : 'free';
      const planCode = isActive ? planDefinition?.id ?? null : null;
      const planLabel = planDefinition?.name ?? (plan === 'free' ? 'Free' : plan === 'lifetime' ? 'Lifetime' : 'Pro');

      return {
        plan,
        planCode,
        planLabel,
        isActive,
        willRenew: !subscription.cancel_at_period_end,
        expiresAt: subscription.current_period_end ? new Date(subscription.current_period_end * 1000) : undefined,
        entitlements: isActive ? ['pro_features'] : [],
        customerId: subscription.customer_id,
        subscriptionId: subscription.subscription_id
      };
    } catch (error) {
      this.trackError(error instanceof Error ? error : new Error(String(error)), 'get_subscription_status');
      return {
        plan: 'free',
        isActive: false,
        entitlements: []
      };
    }
  }
  
  // Get available subscription products
  static async getAvailableProducts(): Promise<StripeProduct[]> {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      return STRIPE_PRODUCTS;
    } catch (error) {
      this.trackError(error instanceof Error ? error : new Error(String(error)), 'get_available_products');
      return [];
    }
  }

  // Health check for Stripe API connectivity
  static async healthCheck(): Promise<boolean> {
    if (!this.isConfigured()) {
      return false;
    }
    if (!isDevelopment()) {
      return true;
    }
    try {
      const res = await fetch('https://js.stripe.com/v3', { method: 'HEAD' });
      return res.ok;
    } catch (error) {
      return false;
    }
  }

  // Centralized error tracking and reporting
  private static trackError(error: Error, context: string, metadata?: Record<string, any>) {
    if (isDevelopment()) {
      console.error(`🚨 [StripeService] ${context}:`, error, metadata);
    }
    // Production monitoring would go here
  }

  // Enhanced error handling for API calls
  private static async handleApiError(error: any, context: string): Promise<never> {
    this.trackError(error instanceof Error ? error : new Error(String(error)), context);

    if (error.message?.includes('Network error')) {
      throw new Error('Network error. Please check your internet connection and try again.');
    }

    if (error.message?.includes('Authentication failed')) {
      throw new Error('Authentication failed. Please sign in again.');
    }

    if (error.message?.includes('Rate limit exceeded')) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }

    throw error;
  }
  
  // Create checkout session for a product
  static async createCheckoutSession(priceId: string, planCode?: string): Promise<{ url: string } | null> {
    // Rate limiting check
    const userId = 'checkout_request'; 
    if (!this.checkRateLimit(userId)) {
      throw new Error('Too many checkout requests. Please wait 15 minutes before trying again.');
    }

    if (!isSupabaseConfigured()) {
      throw new Error('Authentication service is not properly configured.');
    }

    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Please sign in to your account before upgrading your plan.');
      }

      const product = STRIPE_PRODUCTS.find(p => p.priceId === priceId || p.planCode === planCode);
      if (!product) {
        throw new Error('Product not found');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const checkoutUrl = `${supabaseUrl}/functions/v1/stripe-checkout`;

      const requestBody = {
        price_id: priceId,
        mode: product.mode,
        success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/pricing`,
        plan_code: product.planCode
      };

      const response = await fetch(checkoutUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken() || ''
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        } else if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again in a few moments.');
        } else {
          throw new Error(errorData.error || 'Failed to create checkout session.');
        }
      }

      const responseData = await response.json();
      if (!responseData.url) {
        throw new Error('No checkout URL returned');
      }

      return { url: responseData.url };
    } catch (error) {
      return this.handleApiError(error, 'checkout_session_creation');
    }
  }
  
  // Create customer portal session
  static async createPortalSession(): Promise<{ url: string } | null> {
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication service is not properly configured.');
    }

    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Please sign in to your account before managing your subscription.');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const portalUrl = `${supabaseUrl}/functions/v1/stripe-billing-portal`;

      const response = await fetch(portalUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken() || ''
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        } else if (response.status === 404) {
          throw new Error('Customer not found. Please contact support.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again in a few moments.');
        } else {
          throw new Error(errorData.error || 'Failed to create portal session.');
        }
      }

      const responseData = await response.json();
      if (!responseData.url) {
        throw new Error('No portal URL returned');
      }

      return { url: responseData.url };
    } catch (error) {
      return this.handleApiError(error, 'portal_session_creation');
    }
  }
  
  // Instance methods (keeping for backward compatibility)
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    return StripeService.getSubscriptionStatus();
  }
  
  async getAvailableProducts(): Promise<StripeProduct[]> {
    return StripeService.getAvailableProducts();
  }
  
  async createCheckoutSession(priceId: string): Promise<{ url: string } | null> {
    return StripeService.createCheckoutSession(priceId);
  }
  
  async createPortalSession(): Promise<{ url: string } | null> {
    return StripeService.createPortalSession();
  }
}
